import React, { useState, useContext, useLayoutEffect, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ApiProxyGroup from "../../../app/api/admin/proxy-group/ApiProxyGroup";
import ApiAppInit from "../../../app/api/admin/system/ApiAppInit";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import InputWrapper from "../../components/input/__wrapper";
import InputRow from "../../components/input/__row";
import InputText from "../../components/inputs/text";
import InputDate from "../../components/inputs/date";
import InputCheckbox from "../../components/inputs/checkbox";
import Button from "../../components/button";
import { groupProxiesUrl } from "./group";
import "./styles.css";

// Карточка группы прокси: show / edit / create (режим — contextContent.type). Поля — название и
// срок (срок только для информации: панель по нему ничего не делает). Удалить можно только пустую
// группу — иначе бэкенд отвечает 409 с текстом, он приходит тостом.
export default function ProxyGroupShow({ ...other }) {
    return <Page ApiModel={ApiProxyGroup} {...other}>
        <ProxyGroupShowContent/>
    </Page>;
}

function ProxyGroupShowContent() {
    const contextContent = useContext(ContextContent);

    const [_id, set_id] = useState('');
    const [_name, set_name] = useState('');
    const [_expiresAt, set_expiresAt] = useState('');
    const [_proxiesCount, set_proxiesCount] = useState(0);
    const [_nodeIds, set_nodeIds] = useState([]);

    contextContent._dataToServer.current = {
        name: _name,
        expires_at: _expiresAt,
    };

    const setData = (data) => {
        set_id(data.id);
        set_name(data.name ?? '');
        set_expiresAt(data.expires_at ?? '');
        set_proxiesCount(data.proxies_count ?? 0);
        set_nodeIds(data.node_ids ?? []);
        contextContent.setTitle(`Группа прокси: ${data.name}`);
    };

    // Справочник групп (app-init) хранит только подпись «название (до даты)» — перечитываем его,
    // лишь когда она могла измениться: группа только что создана или сохранены название / срок.
    // Состав (прокси, ноды) в подписи не участвует. Удалённую группу уберёт список групп — он
    // перечитывает справочник, когда на него возвращаются.
    const isCreatedRef = useRef(contextContent.isCreate);
    const savedLabelRef = useRef(null);

    useLayoutEffect(() => {
        contextContent._handleSetData.current = (data) => {
            setData(data);
            const label = `${data.name}|${data.expires_at}`;
            if (isCreatedRef.current || ((savedLabelRef.current !== null) && (savedLabelRef.current !== label))) {
                ApiAppInit.ReloadProxyGroups();
            }
            isCreatedRef.current = false;
            savedLabelRef.current = label;
        };
        if (contextContent.isCreate) contextContent.setTitle('Новая группа прокси');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Загрузка — на каждую смену записи, а не один раз при монтировании: после «Создать» страница
    // не пересоздаётся (роуты create и :id рисуют тот же компонент), и без этого карточка новой
    // группы осталась бы пустой, с id '' в запросах блока нод. doLoad меняется вместе с id и типом.
    const doLoad = contextContent.doLoad;
    useEffect(() => { doLoad(); }, [doLoad]);

    // Ответ привязки нод — группа с новыми node_ids и счётчиками: он и есть свежие данные сервера.
    const handleNodesSaved = (data) => {
        contextContent._dataFromServer.current = { ...contextContent._dataFromServer.current, ...data };
        set_nodeIds(data.node_ids ?? []);
        set_proxiesCount(data.proxies_count ?? 0);
    };

    const readOnly = !contextContent.isCreateOrEdit;
    const errors = contextContent.errors ?? {};

    return <>
        {!contextContent.isCreate ?
            <InputWrapper>
                <InputText className="input_wf" value={_id} readOnly={true}>id</InputText>
            </InputWrapper> : null}

        <InputRow>
            <InputWrapper>
                <InputText className="input_wf" value={_name} readOnly={readOnly} errors={errors.name}
                    placeholder="напр. Пачка октябрь" onChange={(e) => set_name(e.target.value)}>Название</InputText>
            </InputWrapper>
            <InputWrapper>
                <InputDate className="input_wf" value={_expiresAt} readOnly={readOnly} errors={errors.expires_at}
                    onChange={(e) => set_expiresAt(e.target.value)}>До (для информации)</InputDate>
            </InputWrapper>
        </InputRow>

        {!contextContent.isCreate ? <>
            <div className="proxy-groups__block">
                <div className="proxy-groups__block-title">Прокси группы</div>
                {_proxiesCount > 0
                    ? <Link to={groupProxiesUrl(_id)}>Прокси в группе: {_proxiesCount} — открыть список</Link>
                    : <span className="proxy-groups__hint">В группе нет прокси.</span>}
                <div className="proxy-groups__hint">
                    Добавить прокси в группу — в разделе «Прокси»: при импорте или отметить в списке и «В группу».
                </div>
            </div>

            <ProxyGroupNodes
                groupId={_id}
                nodeIds={_nodeIds}
                canEdit={contextContent.checkPermission('edit')}
                onSaved={handleNodesSaved}
            />
        </> : null}
    </>;
}

// Ноды группы: чекбоксы всех нод — имя и IP, «Сохранить» — attach / detach разницы (группы и ноды —
// многие ко многим). Нод немного — список целиком из справочников state.option.nodes (имена) и
// nodeIps (публичные IP; отдельным словарём, чтобы подписи нод в выпадающих списках не менялись).
function ProxyGroupNodes({ groupId, nodeIds, canEdit, onSaved }) {
    const nodes = useSelector((state) => state.option.nodes);
    const nodeIps = useSelector((state) => state.option.nodeIps);
    const [_selectedIds, set_selectedIds] = useState(nodeIds);
    const [_isSaving, set_isSaving] = useState(false);

    // Пришли новые данные сервера (загрузка, сохранение) — отметки берём из них.
    useEffect(() => { set_selectedIds(nodeIds); }, [nodeIds]);

    const sortedNodes = useMemo(
        () => Object.entries(nodes).sort(([, leftName], [, rightName]) => String(leftName).localeCompare(String(rightName))),
        [nodes],
    );

    const addedIds = _selectedIds.filter((id) => !nodeIds.includes(id));
    const removedIds = nodeIds.filter((id) => !_selectedIds.includes(id));
    const isChanged = (addedIds.length > 0) || (removedIds.length > 0);

    const toggle = (id, isChecked) => {
        set_selectedIds((previous) => (isChecked ? [...previous, id] : previous.filter((item) => item !== id)));
    };

    const handleSave = async () => {
        if (_isSaving || !isChanged) return;
        set_isSaving(true);
        try {
            let data = null;
            if (addedIds.length > 0) data = await ApiProxyGroup.AttachNodes(groupId, addedIds).getResponse();
            // Привязка не прошла — отвязку не шлём. Отвязка не прошла — привязка уже на сервере:
            // её ответ не теряем, иначе отметки показывали бы привязанную ноду несохранённой.
            if ((removedIds.length > 0) && ((addedIds.length === 0) || (data !== null))) {
                const detached = await ApiProxyGroup.DetachNodes(groupId, removedIds).getResponse();
                if (detached) data = detached;
            }
            if (data) onSaved(data);
        } finally {
            set_isSaving(false);
        }
    };

    return <div className="proxy-groups__block">
        <div className="proxy-groups__block-title">Ноды группы</div>
        {sortedNodes.length === 0 ? <div className="proxy-groups__hint">Нод нет.</div> : null}
        <div className="proxy-groups__nodes">
            {sortedNodes.map(([id, name]) => {
                const nodeId = Number(id);
                return <InputCheckbox key={id} value={_selectedIds.includes(nodeId)} readOnly={!canEdit || _isSaving}
                    onChange={(e) => toggle(nodeId, e.target.checked)}>
                    {name}
                    {nodeIps[id] ? <span className="proxy-groups__node-ip">{nodeIps[id]}</span> : null}
                </InputCheckbox>;
            })}
        </div>
        {canEdit ? <div className="proxy-groups__actions">
            <Button className="btn-success" disabled={!isChanged || _isSaving} onClick={handleSave}>
                {_isSaving ? 'Сохранение…' : 'Сохранить ноды'}
            </Button>
            {isChanged ? <Button disabled={_isSaving} onClick={() => set_selectedIds(nodeIds)}>Отменить</Button> : null}
        </div> : null}
    </div>;
}
